import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";
import ActiveIcon from "@material-ui/icons/Check";
import { notBlank } from "../util/ext";
import { Link, useRouteMatch } from "react-router-dom";
import Tooltip from "@material-ui/core/Tooltip";
import { useProjectDelete } from "./cspServices";
import { AlertContext } from "../components/alert/AlertProvider";
import { Box, IconButton } from "@material-ui/core";
import { equals, not } from "ramda";
import ConfirmButton from "../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
  },

  contentCell: {
    width: "33%",
  },

  cell: {
    minWidth: 135,
  },
}));

const ProgramTable = ({ program, projects, onScrollVisible }) => {
  const { url } = useRouteMatch();
  const classes = useStyles();
  const { alertError } = useContext(AlertContext);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  const [processing, setProcessing] = useState(false);
  const projectDelete = useProjectDelete();
  const handleDeleteClick = useCallback(
    async projectId => {
      setProcessing(true);
      try {
        await projectDelete.mutateAsync({
          resourceIds: { id: projectId },
        });
      } catch (err) {
        alertError(err);
      } finally {
        setProcessing(false);
      }
    },
    [projectDelete, alertError],
  );

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer
        ref={tableContainerEl}
        style={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
      >
        <Table stickyHeader aria-label="Projects">
          <TableHead>
            <TableRow>
              <TableCell
                key="project"
                align="left"
                className={classes.contentCell}
              >
                Name
              </TableCell>

              <TableCell
                key="project_key"
                align="left"
                className={classes.cell}
              >
                Project Key
              </TableCell>

              <TableCell
                key="project_active"
                align="center"
                className={classes.cell}
              >
                Active
              </TableCell>

              <TableCell
                key="actions-edit"
                align="center"
                style={{ width: 50 }}
              >
                Edit
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {projects.map(project => (
              <TableRow hover tabIndex={-1} key={project.projectId}>
                <TableCell
                  key={"name:" + project.projectId}
                  align="left"
                  className={classes.contentCell}
                >
                  {project.projectName}
                </TableCell>

                <TableCell
                  key={"key:" + project.projectId}
                  align="left"
                  className={classes.contentCell}
                >
                  {project.projectKey}
                </TableCell>

                <TableCell
                  key={"active:" + project.projectId}
                  align="center"
                  className={classes.contentCell}
                >
                  {project.active && (
                    <ActiveIcon fontSize="large" color="primary" />
                  )}
                </TableCell>

                <TableCell
                  key={"actions-edit:" + project.projectId}
                  align="center"
                  style={{ width: 75 }}
                >
                  <Box display="flex" alignItems="center">
                    <Tooltip title="Edit">
                      <Link
                        component={IconButton}
                        to={{
                          pathname: `${url}/projects/${project.projectId}`,
                          state: {
                            label: `Back to ${program.name}`,
                            previousLocation: `/manage-csp/programs/${program.id}`,
                          },
                        }}
                      >
                        <EditIcon fontSize="large" color="primary" />
                      </Link>
                    </Tooltip>
                    {not(equals(program.key, "project-trash-can")) && (
                      <ConfirmButton
                        disabled={processing}
                        handleDelete={() =>
                          handleDeleteClick(project.projectId)
                        }
                        dialogTitle="Delete Project"
                        dialogBody="Are you sure you want to delete this project?"
                      />
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ProgramTable;
